import { DocumentStatusBadge } from '@/components/badges/DocumentStatusBadge';
import { type SpaceFilter, STATUS_FILTER_LABELS, type StatusFilter } from '@/components/filters/documentStatus';
import { useDocumentListFilters } from '@/components/filters/useDocumentListFilters';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { considerationTypesState, spacesState, taskTypesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type ConsiderationType, DocumentStatusSchema, type TaskType } from '@rock/types';
import { keyBy, orderBy } from 'lodash';
import { type FC, type MouseEvent, useMemo } from 'react';
import { TaskCadenceCells, TaskCadenceHeads } from './TaskCadenceCells';

/** Every task type across considerations, with its cadence as parsed and its effort. */
export const TaskTypesPage: FC = () => {
    const taskTypes = useAsyncAtomValue(taskTypesState, []);
    const considerationTypes = useAsyncAtomValue(considerationTypesState, []);
    const spaces = useAsyncAtomValue(spacesState, []);
    const onRowClick = useRowClick();
    const { statusFilter, spaceFilter, setStatusFilter, setSpaceFilter } = useDocumentListFilters();

    const sortedSpaces = useMemo(() => orderBy(spaces, (s) => s.name.toLowerCase()), [spaces]);
    const considerationsById = useMemo(() => keyBy(considerationTypes, (type) => type.id), [considerationTypes]);

    const rows = useMemo(() => {
        const filtered = taskTypes.filter((taskType) => {
            if (statusFilter !== 'all' && taskType.documentStatus !== statusFilter) return false;
            const spaceId = considerationsById[taskType.considerationTypeId]?.spaceId;
            if (spaceFilter !== 'all' && spaceId !== spaceFilter) return false;
            return true;
        });
        return orderBy(filtered, [
            (taskType) => taskType.title.toLowerCase(),
            (taskType) => considerationsById[taskType.considerationTypeId]?.title.toLowerCase() ?? '',
        ]);
    }, [taskTypes, considerationsById, statusFilter, spaceFilter]);

    return (
        <PageShell>
            <PageHeader title="Tasks" description="Every task across considerations, with how often it comes up.">
                <Select value={spaceFilter} onValueChange={(value) => setSpaceFilter(value as SpaceFilter)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Spaces</SelectItem>
                        {sortedSpaces.map((space) => (
                            <SelectItem key={space.id} value={space.id}>
                                {space.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
                    <SelectTrigger className="w-[160px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {DocumentStatusSchema.options.map((status) => (
                            <SelectItem key={status} value={status}>
                                {STATUS_FILTER_LABELS[status]}
                            </SelectItem>
                        ))}
                        <SelectItem value="all">{STATUS_FILTER_LABELS.all}</SelectItem>
                    </SelectContent>
                </Select>
            </PageHeader>

            {rows.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No tasks found</CardTitle>
                        <CardDescription>
                            {statusFilter === 'all' && spaceFilter === 'all'
                                ? 'Tasks will appear here once they are synced from Notion.'
                                : 'No tasks match the current filters. Try a different filter.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>Tasks</CardTitle>
                        <CardDescription>Tap a row to review the task details and its parse history.</CardDescription>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-[200px]">Title</TableHead>
                                    <TableHead className="min-w-[180px]">Consideration</TableHead>
                                    <TableHead className="min-w-[100px]">Status</TableHead>
                                    <TaskCadenceHeads />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((taskType) => (
                                    <TaskTypeRow
                                        key={taskType.id}
                                        taskType={taskType}
                                        considerationType={considerationsById[taskType.considerationTypeId]}
                                        onClick={onRowClick(
                                            `/content/considerations/${encodeURIComponent(taskType.considerationTypeId)}/tasks/${encodeURIComponent(taskType.id)}/details`,
                                        )}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </PageShell>
    );
};

const TaskTypeRow: FC<{
    taskType: TaskType;
    considerationType: ConsiderationType | undefined;
    onClick: (event: MouseEvent) => void;
}> = ({ taskType, considerationType, onClick }) => (
    <TableRow className="cursor-pointer transition-colors hover:bg-muted/50" onClick={onClick}>
        <TableCell className="font-medium">{taskType.title}</TableCell>
        <TableCell className="whitespace-normal">
            {considerationType?.title ?? <span className="text-muted-foreground">Unknown</span>}
        </TableCell>
        <TableCell>
            <DocumentStatusBadge status={taskType.documentStatus} />
        </TableCell>
        <TaskCadenceCells taskType={taskType} />
    </TableRow>
);
